// Sync client: starts from cache or snapshot, keeps the socket, sends transactions in order and resends them after reconnects.

import {
  SCHEMA_VERSION, uuidv7, type BootstrapHeader, type BootstrapLine, type Change, type CommandError, type MenuSection,
  type ServerMsg,
} from '@confdeck/core-shared';
import { ndjson, SnapshotDecoder } from './decode.ts';
import { SyncCache } from './persist.ts';
import { Store, type PendingOp, type PendingTx } from './store.ts';

export type ConnState = 'connecting' | 'online' | 'offline';

export interface SyncTimings { start: number; firstFocus?: number; loaded?: number; source?: 'cache' | 'network'; decodeMs?: number; storeMs?: number; waitMs?: number }

/** A rejected transaction with the server's readable reason, for the toast and inline field errors. */
export interface Rejection { tx: PendingTx; error: CommandError }

/** Above this many cached change batches the cache is refreshed from a new snapshot in the background. */
const COMPACT_AFTER = 500;

/** Cached lines parse lazily, one at a time, so the focus type can render before the rest is parsed. */
async function* cachedLines(lines: AsyncIterable<string>): AsyncGenerator<{ line: BootstrapLine; raw: string }> {
  for await (const raw of lines) yield { line: JSON.parse(raw) as BootstrapLine, raw };
}

/** Rows decoded and stored in one go; the browser gets the thread back once a run of them takes longer than this. */
const SLICE = 1000, YIELD_AFTER_MS = 30;
/** A records or links line in parts of SLICE rows, in order: the decoder numbers records as they come, so the parts keep the indexes. */
function* slices(l: BootstrapLine): Generator<BootstrapLine> {
  if ((l.t !== 'records' && l.t !== 'links') || l.rows.length <= SLICE) { yield l; return; }
  for (let i = 0; i < l.rows.length; i += SLICE) yield { ...l, rows: l.rows.slice(i, i + SLICE) } as BootstrapLine;
}
/** Hands the thread to the browser for input and paint, then continues. */
const yieldToMain = (): Promise<void> => new Promise((r) => { const ch = new MessageChannel(); ch.port1.onmessage = () => r(); ch.port2.postMessage(null); });

export class SyncClient {
  readonly store = new Store();
  header: BootstrapHeader | null = null;
  menu: MenuSection[] = [];
  state: ConnState = 'connecting';
  readonly timings: SyncTimings = { start: performance.now() };
  readonly presence = new Map<string, string | null>();
  onReject: (r: Rejection) => void = () => {};
  /**
   * A refusal the person can fix — fields a stage asks for, a reason (R-C14, R-C15): the screen asks and gives the change to send
   * again, or null when the person gave up. Returns null at once when the refusal is not of that kind.
   */
  fixer: ((r: Rejection) => Promise<Pick<PendingTx, 'command' | 'input' | 'ops' | 'reason'> | null> | null) | null = null;
  /** Unsent edits could not be saved in the browser: they live only in this tab until the server confirms them. */
  onStorageError: (e: Error) => void = (e) => console.error('Не удалось сохранить правки в браузере', e);
  private socket: WebSocket | null = null;
  private cache: SyncCache;
  private sentUnacked = new Set<string>();
  private waiters = new Map<string, (r: { ok: true; syncId: number } | { ok: false; error: CommandError }) => void>();
  private retry = 0;
  private logBuffer: Change[] = [];
  private logTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private readonly base: string;
  private focusView: string | null = null;
  private focusQuery(focusTypeId: string | null) {
    const p = new URLSearchParams();
    if (focusTypeId) p.set('focus', focusTypeId);
    if (this.focusView) p.set('view', this.focusView);
    const q = p.toString();
    return q ? `?${q}` : '';
  }

  constructor(opts: { base?: string; scope: string }) {
    this.base = opts.base ?? '';
    this.cache = new SyncCache(opts.scope);
  }

  /**
   * Starts the client. `focusTypeId` is the section the user opened: its records load first and
   * `onFocusReady` fires as soon as they are in the store, before the rest of the snapshot.
   */
  async start(focusTypeId: string | null, userId: string, onFocusReady?: () => void, focusViewId?: string | null): Promise<void> {
    this.focusView = focusViewId ?? null;
    const cached = await this.cache.load();
    if (cached && cached.header.userId === userId && cached.header.schemaVersion === SCHEMA_VERSION) {
      // The header (menu, views) comes first, as from the network: the opened section draws as soon as its records are in,
      // not after the whole cache (a warm start waited ~2 s for 150 thousand records, audit 28.10 L3).
      this.header = cached.header;
      this.menu = cached.header.menu;
      await this.loadLines(cachedLines(cached.lines), focusTypeId, onFocusReady);
      this.store.syncId = cached.header.syncId;
      this.store.applyChanges(cached.changes);
      this.store.syncId = Math.max(this.store.syncId, cached.syncId);
      for (const tx of await this.cache.loadOutbox(userId)) this.store.addPending({ ...tx, ackSyncId: undefined });
      this.timings.source = 'cache';
      this.timings.loaded = performance.now();
      if ((await this.cache.changeCount()) > COMPACT_AFTER) void this.refreshCache(focusTypeId);
    } else {
      // Unsent edits of the same person survive a schema change or a resync; another person's are dropped.
      if (cached) await this.cache.clear(cached.header.userId === userId);
      await this.fromNetwork(focusTypeId, onFocusReady);
      for (const tx of await this.cache.loadOutbox(userId)) this.store.addPending({ ...tx, ackSyncId: undefined });
    }
    this.store.loaded();
    this.connect();
  }

  private async loadLines(lines: AsyncIterable<{ line: BootstrapLine; raw: string }> | Iterable<{ line: BootstrapLine; raw: string }>, focusTypeId: string | null, onFocusReady?: () => void, keep?: string[]) {
    const dec = new SnapshotDecoder();
    let focusSeen = false;
    let focusDone = false;
    // Phase times for the speed readout: waiting for bytes and JSON parsing, decoding, loading into the store.
    let decodeMs = 0, storeMs = 0, waitMs = 0;
    let tw = performance.now();
    let lastYield = performance.now();
    let lastTouch = 0;
    for await (const { line: l, raw } of lines as AsyncIterable<{ line: BootstrapLine; raw: string }>) {
      waitMs += performance.now() - tw;
      if (keep && l.t !== 'header') keep.push(raw);
      if (l.t === 'header') { this.header = l; this.menu = l.menu; this.store.syncId = l.syncId; tw = performance.now(); continue; }
      // A line of 5 000 records or 20 000 links is taken in parts, and the browser gets the thread back between them: loading the
      // rest of the snapshot does not freeze scrolling and clicks for hundreds of milliseconds (plan L3, long tasks up to 1 s).
      for (const part of slices(l)) {
        let t = performance.now();
        const objs = dec.decode(part);
        decodeMs += performance.now() - t;
        t = performance.now();
        if (objs.length) this.store.loadBase(objs);
        storeMs += performance.now() - t;
        if (performance.now() - lastYield > YIELD_AFTER_MS) { await yieldToMain(); lastYield = performance.now(); }
      }
      Object.assign(this.timings, { decodeMs, storeMs, waitMs });
      // The URL names the section by type key; the id becomes known with the schema line.
      if (l.t === 'meta' && focusTypeId) focusTypeId = this.store.meta.typeByKey(focusTypeId)?.id ?? focusTypeId;
      // The focus type arrives sorted like its view: the first page is already the top of the table, so
      // the screen paints after the first page and fills in as the rest arrives.
      if (l.t === 'records' && (l.typeId === focusTypeId || (!focusTypeId && !focusSeen))) {
        focusSeen = true;
        focusTypeId = l.typeId;
        // The table re-sorts all it has on every touch: the first page paints at once, the rest fill in at most once a second,
        // and `loaded()` gives the final count (20 pages of 5 000 used to mean 20 sorts of a growing list).
        if (!focusDone || performance.now() - lastTouch > 1000) { this.store.touchType(l.typeId); lastTouch = performance.now(); }
        if (!focusDone) {
          focusDone = true;
          this.timings.firstFocus = performance.now();
          onFocusReady?.();
        }
        // Give the browser a frame to paint before decoding the next page.
        await new Promise((r) => setTimeout(r, 0));
      }
      tw = performance.now();
    }
    if (!focusDone) { this.timings.firstFocus = performance.now(); onFocusReady?.(); }
  }

  private async fromNetwork(focusTypeId: string | null, onFocusReady?: () => void) {
    const res = await fetch(`${this.base}/api/sync/bootstrap${this.focusQuery(focusTypeId)}`, { credentials: 'same-origin' });
    if (!res.ok || !res.body) throw new Error(`Не удалось загрузить данные (${res.status})`);
    const keep: string[] = [];
    this.store.reset();
    await this.loadLines(ndjson(res.body), focusTypeId, onFocusReady, keep);
    this.timings.source = 'network';
    this.timings.loaded = performance.now();
    if (this.header) void this.cache.saveSnapshot(this.header, keep);
  }

  /** Background compaction: a fresh snapshot replaces the cache; the store in memory stays as is. */
  private async refreshCache(focusTypeId: string | null) {
    const res = await fetch(`${this.base}/api/sync/bootstrap${this.focusQuery(focusTypeId)}`);
    if (!res.ok || !res.body) return;
    const keep: string[] = [];
    let header: BootstrapHeader | null = null;
    for await (const { line: l, raw } of ndjson(res.body)) { if (l.t === 'header') header = l; else keep.push(raw); }
    if (header) await this.cache.saveSnapshot(header, keep);
  }

  // --- socket --------------------------------------------------------------------------------

  private connect() {
    if (this.closed) return;
    this.setState('connecting');
    const url = new URL(`${this.base}/api/sync/ws`, location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const s = new WebSocket(url);
    this.socket = s;
    s.onopen = () => {
      s.send(JSON.stringify({ t: 'hello', since: this.store.syncId, schemaVersion: SCHEMA_VERSION }));
      if (this.looking) s.send(JSON.stringify({ t: 'presence', recordId: this.looking }));
    };
    s.onmessage = (e) => this.onMessage(JSON.parse(e.data as string) as ServerMsg);
    s.onclose = () => {
      if (this.socket !== s) return;
      this.socket = null;
      this.sentUnacked.clear();
      this.setState('offline');
      // Backoff 0.5 s → 10 s with jitter; the outbox stays and is resent on reconnect.
      const delay = Math.min(10_000, 500 * 2 ** this.retry++) * (0.75 + Math.random() * 0.5);
      setTimeout(() => this.connect(), delay);
    };
  }

  private onMessage(m: ServerMsg) {
    switch (m.t) {
      case 'ready':
        this.retry = 0;
        this.setState('online');
        for (const tx of this.store.pending) if (tx.ackSyncId === undefined) this.sendTx(tx);
        break;
      case 'changes':
        this.store.applyChanges(m.changes);
        this.logChanges(m.changes);
        break;
      case 'ack':
        this.sentUnacked.delete(m.clientTxId);
        this.store.ack(m.clientTxId, m.syncId);
        this.waiters.get(m.clientTxId)?.({ ok: true, syncId: m.syncId });
        this.waiters.delete(m.clientTxId);
        this.noAsk.delete(m.clientTxId);
        // The outbox forgets the edit only after its changes are written to the log (next flush): a page closed in between
        // still has it in the outbox, and re-sending a confirmed transaction is answered as a repeat.
        this.scheduleLogFlush();
        break;
      case 'reject': {
        this.sentUnacked.delete(m.clientTxId);
        const tx = this.store.reject(m.clientTxId);
        this.saveOutbox();
        const w = this.waiters.get(m.clientTxId);
        this.waiters.delete(m.clientTxId);
        // The mark is dropped on every answer, also one without its transaction (MiniMax review wave A, P3-3).
        const ask = !this.noAsk.delete(m.clientTxId);
        const fix = tx && ask ? this.fixer?.({ tx, error: m.error }) : null;
        if (fix) {
          // The same change once more with what was asked; a caller waiting for the answer gets the answer of the retry.
          void fix.then((next) => {
            if (next) { const id = this.mutate(next.command, next.input, next.ops, next.reason); if (w) this.waiters.set(id, w); }
            else if (w) w({ ok: false, error: m.error });
          });
          break;
        }
        if (w) w({ ok: false, error: m.error });
        else if (tx) this.onReject({ tx, error: m.error });
        break;
      }
      case 'resync':
        void this.cache.clear(true).then(() => location.reload());
        break;
      case 'presence':
        if (m.recordId) this.presence.set(m.userId, m.recordId); else this.presence.delete(m.userId);
        this.store.status();
        break;
    }
  }

  private logChanges(changes: Change[]) {
    this.logBuffer.push(...changes);
    this.scheduleLogFlush();
  }

  private scheduleLogFlush() {
    if (this.logTimer) return;
    this.logTimer = setTimeout(() => {
      this.logTimer = null;
      const batch = this.logBuffer;
      this.logBuffer = [];
      void this.cache.appendChanges(batch, this.store.syncId).then(() => this.saveOutbox());
    }, 300);
  }

  private saveOutbox() {
    this.cache.saveOutbox(this.store.pending.filter((p) => p.ackSyncId === undefined), this.header?.userId ?? null).catch((e: unknown) => this.onStorageError(e instanceof Error ? e : new Error(String(e))));
  }

  private setState(s: ConnState) { this.state = s; this.store.status(); }

  private sendTx(tx: PendingTx) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.sentUnacked.has(tx.clientTxId)) return;
    this.sentUnacked.add(tx.clientTxId);
    this.socket.send(JSON.stringify({ t: 'tx', clientTxId: tx.clientTxId, command: tx.command, input: tx.input, ...(tx.reason ? { reason: tx.reason } : {}) }));
  }

  // --- public API ----------------------------------------------------------------------------

  /** Runs a command optimistically: `ops` show at once, the server confirms or the store rolls back. */
  mutate(command: string, input: unknown, ops: PendingOp[] = [], reason?: string): string {
    const tx: PendingTx = { clientTxId: uuidv7(), command, input, ops, ...(reason ? { reason } : {}) };
    this.store.addPending(tx);
    this.saveOutbox();
    this.sendTx(tx);
    return tx.clientTxId;
  }

  /**
   * Runs a command and waits for the server's answer — for actions whose result the screen must show
   * (a form keeps its values and shows the reason when the server refuses).
   */
  run(command: string, input: unknown, opts: { ask?: boolean } = {}): Promise<{ ok: true; syncId: number } | { ok: false; error: CommandError }> {
    return new Promise((resolve) => {
      const id = this.mutate(command, input);
      // A change of many records one by one (BulkEdit, R-U30) reports a refusal in its summary instead of asking per record.
      if (opts.ask === false) this.noAsk.add(id);
      this.waiters.set(id, resolve);
    });
  }
  private readonly noAsk = new Set<string>();

  /** The record this tab looks at: kept, so a socket that opens later (a page loaded by its address, a reconnect) still tells it. */
  private looking: string | null = null;
  setPresence(recordId: string | null) {
    this.looking = recordId;
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify({ t: 'presence', recordId }));
  }

  get unsent(): number { return this.store.pending.filter((p) => p.ackSyncId === undefined).length; }

  async resetCache() { await this.cache.clear(); }

  close() { this.closed = true; this.socket?.close(); }
}
